import { to } from 'await-to-js';
import { getToken, removeToken, setToken } from '@/utils/auth';
import { login as loginApi, logout as logoutApi, getInfo as getUserInfo, getCapabilityList, getUserConfig } from '@/api/login';
import { getCapabilityTree } from '@/api/console/deviceType';
import { LoginData } from '@/api/types';
import defAva from '@/assets/images/profile.jpg';
import man from '@/assets/images/user_man.png';
import woman from '@/assets/images/user_fomale.png';
import store from '@/store';
import { listDeviceType } from '@/api/console/deviceType';
import { getConfigValue } from '@/api/system/config';
import cache from '@/plugins/cache';

function buildCapabilityMap(list, map = {}) {
  for (const item of list) {
    map[item.capabilityCode] = item.capabilityName;
    if (item.children && item.children.length) {
      buildCapabilityMap(item.children, map);
    }
  }
  return map;
}
export const useUserStore = defineStore('user', () => {
  const token = ref(getToken());
  const name = ref('');
  const nickname = ref('');
  const phone = ref('');
  const userId = ref<string | number>('');
  const tenantId = ref('');
  const avatar = ref('');
  const roles = ref<Array<string>>([]); // 用户角色编码集合 → 判断路由权限
  const permissions = ref<Array<string>>([]); // 用户权限编码集合 → 判断按钮权限
  const deptId = ref<string>('');
  const deptName = ref<string>('');
  const deptCode = ref<string>('');
  const projectId = ref<bigint>();
  const capabilities = ref<Array<string>>([]);
  const capabMap = ref<Object>({});
  const equipmentInfo = ref<Array<any>>([]); // 设备信息
  const userRole = ref<string>('');
  const userConfig = ref<Object>({});
  const systemInfo = ref<{ name: string; icon: string; screenName: string }>({
    name: '',
    icon: '',
    screenName: ''
  });

  const setTenantId = (id) => {
    tenantId.value = id;
  };
  /**获取设备能力集*/
  const getCapabilities = async () => {
    const [err, res] = await to(getCapabilityList());
    capabilities.value = res?.data || [];
    const [terr, teres] = await to(getCapabilityTree());
    buildCapabilityMap(teres?.data || [], capabMap.value);
  };
  /**
   * 登录
   * @param userInfo
   * @returns
   */
  const login = async (userInfo: LoginData): Promise<void> => {
    const [err, res] = await to(loginApi(userInfo));
    if (res) {
      const data = res.data;
      setToken(data.access_token);
      token.value = data.access_token;
      return Promise.resolve();
    }
    return Promise.reject(err);
  };
  /**获取用户配置inxi */
  const getConfig = async (): Promise<void> => {
    const [err, res] = await to(getUserConfig());
    if (res) {
      const data = res.data;
      userConfig.value = data;
      return Promise.resolve();
    }
    return Promise.reject(err);
  };
  // 获取用户信息
  const getInfo = async (): Promise<void> => {
    const [err, res] = await to(getUserInfo());
    if (res) {
      const data = res.data;
      const user = data.user;
      let temAvatar = user.sex != '1' ? man : woman;
      const profile = user.avatar == '' || user.avatar == null ? temAvatar : user.avatar;
      getCapabilities();
      getConfig();
      if (data.roles && data.roles.length > 0) {
        // 验证返回的roles是否是一个非空数组
        roles.value = data.roles;
        permissions.value = data.permissions;
      } else {
        roles.value = ['ROLE_DEFAULT'];
      }
      if (data.user.roles?.length > 0) {
        userRole.value = data.user.roles.map((role) => role.roleName).join(',');
      }
      name.value = user.userName;
      nickname.value = user.nickName;
      phone.value = user.phonenumber;
      avatar.value = profile;
      userId.value = user.userId;
      tenantId.value = user.tenantId;
      deptId.value = user.deptId;
      deptName.value = user.deptName;
      deptCode.value = user.deptCode;
      projectId.value = user.projectId;

      getSystemNameAndIcon();
      queryEquipmentList();

      return Promise.resolve();
    }
    return Promise.reject(err);
  };

  // 查询设备信息
  const queryEquipmentList = async () => {
    try {
      const {
        data: { rows = [], code }
      } = await listDeviceType({ category: 0 });

      if (code === 200) equipmentInfo.value = rows;
    } catch (error) {
      console.log(error);
    }
  };

  // 获取系统名称和图标
  const getSystemNameAndIcon = async () => {
    const { code, data = [] } = await getConfigValue({
      id: tenantId.value,
      data: ['sys.user.systemName', 'sys.user.systemIcon', 'sys.user.screenName']
    });
    if (code === 200) {
      const name = data[0]?.configValue || '';
      const icon = data[1]?.configValue || '';
      const screenName = data[2]?.configValue || '';
      systemInfo.value.name = name;
      systemInfo.value.icon = icon;
      systemInfo.value.screenName = screenName;
      document.title = name || import.meta.env.VITE_APP_TITLE;
      if (icon) document.querySelector('link[rel="icon"]')?.setAttribute('href', icon);
      // 将系统名称和图标缓存到 localStorage，供登录页及页面标题使用
      if (name) cache.local.set('sys-name', name);
      if (icon) cache.local.set('sys-icon', icon);
    }
  };

  // 注销
  const logout = async (): Promise<void> => {
    await logoutApi();
    token.value = '';
    roles.value = [];
    permissions.value = [];
    removeToken();
    // 清理登录时写入的租户套餐版本与名称，避免退出后水印状态残留到下次登录前。
    localStorage.removeItem('packageEdition');
    localStorage.removeItem('tenantName');
  };

  const setAvatar = (value: string) => {
    avatar.value = value;
  };

  return {
    userId,
    tenantId,
    token,
    nickname,
    phone,
    avatar,
    roles,
    permissions,
    login,
    getInfo,
    logout,
    setAvatar,
    deptId,
    deptName,
    deptCode,
    name,
    projectId,
    setTenantId,
    capabilities,
    getCapabilities,
    capabMap,
    equipmentInfo,
    queryEquipmentList,
    userRole,
    userConfig,
    systemInfo
  };
});

export default useUserStore;
// 非setup
export function useUserStoreHook() {
  return useUserStore(store);
}
